import React, { useCallback, useEffect, useRef, useState } from "react";
import { listSites } from "../api/sites";
import {
  importScadaCsv,
  preflightScadaImport,
  commitScadaImport,
  listImportBatches,
  listImportFiles,
  revokeImportBatch,
  revokeImportFile,
  startImportJob,
  fetchImportJobStatus,
  cancelImportJob,
} from "../api/scada";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const statusClasses = {
  success: "bg-emerald-100 text-emerald-700",
  warning: "bg-amber-100 text-amber-700",
  error: "bg-rose-100 text-rose-700",
  neutral: "bg-slate-100 text-slate-600",
};

const StatusTag = ({ tone = "neutral", children }) => {
  const toneClass = statusClasses[tone] || statusClasses.neutral;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${toneClass}`}>
      {children}
    </span>
  );
};

export default function DataImport() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(() => {
    if (typeof window === "undefined") return "";
    const stored = window.localStorage.getItem("pvapp:lastSiteId");
    return stored ? Number(stored) : "";
  });
  const [csvFile, setCsvFile] = useState(null);
  const [pendingZipFile, setPendingZipFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyFilters, setHistoryFilters] = useState({ from: "", to: "" });
  const [expandedBatches, setExpandedBatches] = useState([]);
  const [batchFiles, setBatchFiles] = useState({});
  const [preflightDialogOpen, setPreflightDialogOpen] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [revokingBatchId, setRevokingBatchId] = useState(null);
  const [revokingFileId, setRevokingFileId] = useState(null);
  const [notice, setNotice] = useState("");
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, files: [] });
  const [importJobId, setImportJobId] = useState(null);
  const [importJobStatus, setImportJobStatus] = useState(null);
  const csvInputRef = useRef(null);
  const zipInputRef = useRef(null);

  useEffect(() => {
    const loadSites = async () => {
      try {
        const data = await listSites();
        const allSites = data || [];
        setSites(allSites);
        if (allSites.length === 0) {
          setSelectedSite("");
          return;
        }
        setSelectedSite((current) => {
          if (current && allSites.some((site) => site.id === current)) {
            return current;
          }
          return allSites[0].id;
        });
      } catch (err) {
        console.error("Failed to load sites", err);
      }
    };
    loadSites();
  }, []);

  useEffect(() => {
    if (!selectedSite) return;
    if (typeof window !== "undefined") {
      window.localStorage.setItem("pvapp:lastSiteId", String(selectedSite));
    }
  }, [selectedSite]);

  // Clear any lingering ZIP selection on mount (helps reset button label)
  useEffect(() => {
    setPendingZipFile(null);
    setPreflightResult(null);
    setPreflightError("");
    if (zipInputRef.current) {
      zipInputRef.current.value = "";
    }
  }, []);

  const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "—");
  const toIsoString = (value) => (value ? new Date(value).toISOString() : undefined);

  const loadHistory = useCallback(async () => {
    if (!selectedSite) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const data = await listImportBatches({
        siteId: selectedSite,
        from: toIsoString(historyFilters.from),
        to: toIsoString(historyFilters.to),
      });
      setHistory(data || []);
    } catch (err) {
      console.error("Failed to load import history", err);
      setHistory([]);
      setHistoryError(err.response?.data?.detail || "Failed to load import history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilters.from, historyFilters.to, selectedSite]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!importJobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await fetchImportJobStatus(importJobId);
        if (cancelled) return;
        setImportJobStatus(data);
        if (!["running", "pending"].includes(data.status)) {
          setImportJobId(null);
          await loadHistory();
        }
      } catch (err) {
        console.error("Failed to fetch import job status", err);
      }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [importJobId, loadHistory]);


  const fetchBatchFiles = useCallback(async (batchId) => {
    setBatchFiles((prev) => ({
      ...prev,
      [batchId]: { status: "loading", entries: prev[batchId]?.entries || [] },
    }));
    try {
      const data = await listImportFiles(batchId);
      setBatchFiles((prev) => ({
        ...prev,
        [batchId]: { status: "ready", entries: data || [] },
      }));
    } catch (err) {
      console.error("Failed to load batch files", err);
      setBatchFiles((prev) => ({
        ...prev,
        [batchId]: { status: "error", entries: [] },
      }));
    }
  }, []);

  const handleToggleBatch = (batchId) => {
    if (!expandedBatches.includes(batchId) && !batchFiles[batchId]) {
      fetchBatchFiles(batchId);
    }
    setExpandedBatches((prev) =>
      prev.includes(batchId) ? prev.filter((id) => id !== batchId) : [...prev, batchId]
    );
  };

  const getBatchWarningsCount = (batchId) => {
    const filesState = batchFiles[batchId];
    if (!filesState || filesState.status !== "ready") return null;
    return filesState.entries.reduce((acc, file) => acc + (file.warnings?.length || 0), 0);
  };

  const renderWarningsCell = (batch) => {
    if (batch.revoked_at) {
        return (
          <StatusTag tone="error">
            Revoked{batch.revoked_rows ? ` (${batch.revoked_rows} rows)` : ""}
          </StatusTag>
        );
    }
    const filesState = batchFiles[batch.id];
    if (!filesState) {
      return <span className="text-xs text-slate-500">Awaiting details</span>;
    }
    if (filesState.status === "loading") {
      return <span className="text-xs text-slate-500">Loading…</span>;
    }
    if (filesState.status === "error") {
      return <StatusTag tone="error">Failed</StatusTag>;
    }
    const count = getBatchWarningsCount(batch.id);
    if (!count) {
      return <StatusTag tone="success">None</StatusTag>;
    }
    return <StatusTag tone="warning">{count} warning{count === 1 ? "" : "s"}</StatusTag>;
  };

  const renderBatchFilesTable = (batch) => {
    const filesState = batchFiles[batch.id];
    if (!filesState || filesState.status === "loading") {
      return <div className="py-4 text-sm text-slate-500">Loading file details…</div>;
    }
    if (filesState.status === "error") {
      return <div className="py-4 text-sm text-rose-600">Failed to load file details.</div>;
    }
    if (filesState.entries.length === 0) {
      return <div className="py-4 text-sm text-slate-500">No files recorded for this batch.</div>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">tid</th>
              <th className="text-left px-3 py-2">File name</th>
              <th className="text-left px-3 py-2">Rows inserted</th>
              <th className="text-left px-3 py-2">Warnings</th>
              <th className="text-left px-3 py-2">Created</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filesState.entries.map((file) => {
              const warnings = file.warnings || [];
              const isRevoked = Boolean(file.revoked_at);
              return (
                <tr key={file.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{file.tid || "—"}</td>
                  <td className="px-3 py-2">{file.file_name || "—"}</td>
                  <td className="px-3 py-2">
                    {file.rows_inserted ?? 0}
                    {isRevoked && (
                      <span className="ml-2 text-xs text-rose-600">
                        − {file.rows_deleted || 0} revoked
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isRevoked ? (
                      <StatusTag tone="error">Revoked</StatusTag>
                    ) : warnings.length > 0 ? (
                      <ul className="list-disc pl-4 space-y-1 text-xs text-amber-700">
                        {warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    ) : (
                      <StatusTag tone="success">None</StatusTag>
                    )}
                  </td>
                  <td className="px-3 py-2">{formatDateTime(file.created_at)}</td>
                  <td className="px-3 py-2">
                    {isRevoked ? (
                      <span className="text-xs font-semibold text-rose-600">Revoked</span>
                    ) : (
                      <Button
                        onClick={() => handleRevokeFile(batch.id, file.id)}
                        disabled={revokingFileId === file.id}
                        className="border border-rose-400 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-600 shadow-sm transition hover:bg-rose-500 hover:text-white"
                      >
                        {revokingFileId === file.id ? "Revoking…" : "Revoke file"}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => setHistoryFilters({ from: "", to: "" });

  const handleRevokeBatch = async (batchId) => {
    if (!window.confirm("Revoke this batch and delete all associated SCADA data?")) return;
    setRevokingBatchId(batchId);
    try {
      const response = await revokeImportBatch(batchId);
      setBatchFiles((prev) => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
      await loadHistory();
      setNotice(`Batch #${batchId} revoked (${response.rows_deleted || 0} rows deleted).`);
    } catch (err) {
      console.error("Failed to revoke batch", err);
      setError(err.response?.data?.detail || "Failed to revoke batch.");
    } finally {
      setRevokingBatchId(null);
    }
  };

  const handleRevokeFile = async (batchId, fileId) => {
    if (!window.confirm("Revoke this file and delete its SCADA rows?")) return;
    setRevokingFileId(fileId);
    try {
      const response = await revokeImportFile(fileId);
      await fetchBatchFiles(batchId);
      await loadHistory();
      setNotice(`File #${fileId} revoked (${response.rows_deleted || 0} rows deleted).`);
    } catch (err) {
      console.error("Failed to revoke file", err);
      setError(err.response?.data?.detail || "Failed to revoke file.");
    } finally {
      setRevokingFileId(null);
    }
  };

  const handleCsvUpload = async () => {
    if (!selectedSite) {
      setError("Select a site first.");
      return;
    }
    if (!csvFile) {
      setError("Select a CSV file to upload.");
      return;
    }
    setCsvUploading(true);
    setError("");
    try {
      await importScadaCsv(selectedSite, csvFile);
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = "";
      await loadHistory();
    } catch (err) {
      console.error("CSV upload failed", err);
      setError(err.response?.data?.detail || "CSV import failed. Check the console/logs for details.");
    } finally {
      setCsvUploading(false);
    }
  };

  const runZipPreflight = useCallback(
    async (fileToValidate) => {
      if (!selectedSite || !fileToValidate) return;
      setPreflightDialogOpen(true);
      setPreflightLoading(true);
      setPreflightError("");
      setPreflightResult(null);
      try {
        const result = await preflightScadaImport({
          siteId: selectedSite,
          file: fileToValidate,
        });
        if (!result || Object.keys(result || {}).length === 0) {
          setPreflightError("Empty response received from the server during validation.");
        } else {
          setPreflightResult(result);
        }
      } catch (err) {
        console.error("ZIP preflight failed", err);
        const detail = err.response?.data?.detail;
        const isNetworkError = err?.message?.toLowerCase?.().includes("network");
        setPreflightError(
          detail ||
            (isNetworkError
              ? "Cannot reach the backend (CORS / network error). Check API URL and CORS settings."
              : "ZIP integrity check failed.")
        );
      } finally {
        setPreflightLoading(false);
      }
    },
    [selectedSite]
  );

  const handleZipFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setPendingZipFile(file);
    if (file) {
      runZipPreflight(file);
    } else {
      setPreflightResult(null);
      setPreflightError("");
    }
  };

  const handleZipValidateClick = () => {
    if (!pendingZipFile) {
      setError("Select a ZIP file to validate.");
      return;
    }
    runZipPreflight(pendingZipFile);
  };

  const handleZipCommit = async ({ overwrite = false } = {}) => {
    if (!selectedSite) {
      setError("Select a site first.");
      return;
    }
    if (!pendingZipFile) {
      setError("Select a ZIP file to import.");
      return;
    }
    setCommitLoading(true);
    setError("");
    try {
      const job = await startImportJob({
        siteId: selectedSite,
        file: pendingZipFile,
        overwriteDuplicates: overwrite,
      });
      if (zipInputRef.current) zipInputRef.current.value = "";
      setPendingZipFile(null);
      setPreflightResult(null);
      setPreflightDialogOpen(false);
      setImportJobId(job.job_id);
      setImportJobStatus(job);
      setNotice("Import started…");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "duplicates_detected") {
        setDuplicateDialog({ open: true, files: detail.files || [] });
        setCommitLoading(false);
        return;
      }
      console.error("ZIP import failed", err);
      setError(detail || "ZIP import failed. Please review the logs.");
    } finally {
      setCommitLoading(false);
    }
  };

  const handleCancelJob = async () => {
    if (!importJobId) return;
    try {
      const data = await cancelImportJob(importJobId);
      setImportJobStatus(data);
      setImportJobId(null);
    } catch (err) {
      console.error("Failed to cancel import job", err);
    }
  };

  const handleOverwriteConfirm = () => {
    setDuplicateDialog({ open: false, files: [] });
    handleZipCommit({ overwrite: true });
  };

  const fileProgressPct = importJobStatus?.total_files
    ? Math.min(100, Math.round(((importJobStatus.processed_files || 0) / importJobStatus.total_files) * 100))
    : 0;
  const dayProgressPct = importJobStatus?.current_file_days_total
    ? Math.min(
        100,
        Math.round(((importJobStatus.current_file_days_done || 0) / importJobStatus.current_file_days_total) * 100)
      )
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Data Import / Export</h1>
          <p className="text-slate-600">
            Upload SCADA CSV files directly or bundle several days into a ZIP archive. Files must contain the columns
            tid, timestamp, and last.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <label className="text-sm font-medium text-slate-600">
            Target site
            <select
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
              value={selectedSite || ""}
              onChange={(e) => setSelectedSite(Number(e.target.value))}
            >
              {sites.map((site) => (
                <option value={site.id} key={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="text-sm text-rose-600">{error}</div>}
      {notice && <div className="text-sm text-emerald-600">{notice}</div>}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="font-semibold text-slate-800">Single CSV import</div>
            <p className="text-sm text-slate-500">
              Ideal for quick tests or exporting one day from your SCADA historian.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              id="csv-file"
              type="file"
              ref={csvInputRef}
              accept=".csv,text/csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="block w-full border rounded-md px-3 py-2 text-sm"
            />
            <Button onClick={handleCsvUpload} disabled={csvUploading || !selectedSite}>
              {csvUploading ? "Uploading…" : "Upload CSV"}
            </Button>
            <ul className="list-disc text-sm text-slate-500 pl-5 space-y-1">
              <li>Delimiter must be ';'.</li>
              <li>Timestamp column should be UTC ISO strings.</li>
              <li>Only rows with matching signal mappings are stored.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="font-semibold text-slate-800">ZIP import (batch)</div>
            <p className="text-sm text-slate-500">Bundle multiple CSV files into a ZIP to ingest a backlog.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              id="zip-file"
              type="file"
              ref={zipInputRef}
              accept=".zip,application/zip"
              onChange={handleZipFileChange}
              className="block w-full border rounded-md px-3 py-2 text-sm"
            />
            <Button onClick={handleZipValidateClick} disabled={!selectedSite || !pendingZipFile || preflightLoading}>
              {preflightLoading ? "Validating…" : pendingZipFile ? "Re-run validation" : "Validate ZIP"}
            </Button>
            <ul className="list-disc text-sm text-slate-500 pl-5 space-y-1">
              <li>ZIP must include <code>settings.csv</code> with tids plus optional start/end dates.</li>
              <li>Each CSV inside the ZIP needs tid / timestamp / last columns using a semicolon delimiter.</li>
              <li>Preflight runs automatically after selecting a file; use Re-run validation to refresh.</li>
            </ul>
            {importJobStatus && (
              <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <div className="flex justify-between">
                  <span>Status</span>
                  <span className="font-semibold">{importJobStatus.status}</span>
                </div>
                {importJobStatus.message && (
                  <div className="text-xs text-rose-600">{importJobStatus.message}</div>
                )}
                <div>
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Files</span>
                    <span>
                      {importJobStatus.processed_files || 0} / {importJobStatus.total_files || 0}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-200">
                    <div className="h-2 rounded-full bg-blue-600" style={{ width: `${fileProgressPct}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Days (current file)</span>
                    <span>
                      {importJobStatus.current_file_days_done || 0} / {importJobStatus.current_file_days_total || 0}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-200">
                    <div className="h-2 rounded-full bg-indigo-500" style={{ width: `${dayProgressPct}%` }} />
                  </div>
                </div>
                {importJobStatus.current_file && (
                  <div className="text-xs text-slate-600">Current file: {importJobStatus.current_file}</div>
                )}
                {["running", "pending"].includes(importJobStatus.status) && (
                  <Button
                    onClick={handleCancelJob}
                    className="w-full bg-slate-200 text-slate-900 hover:bg-slate-300 border border-slate-300"
                  >
                    Cancel job
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="font-semibold text-slate-800">Import history</div>
              <p className="text-sm text-slate-500">
                Validated batches for{" "}
                {sites.find((site) => site.id === selectedSite)?.name || "selected site"}.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="text-xs font-medium text-slate-600">
                From
                <input
                  type="date"
                  value={historyFilters.from}
                  onChange={(e) => handleHistoryFilterChange("from", e.target.value)}
                  className="mt-1 block rounded-md border px-3 py-2 text-sm"
                />
              </label>
              <label className="text-xs font-medium text-slate-600">
                To
                <input
                  type="date"
                  value={historyFilters.to}
                  onChange={(e) => handleHistoryFilterChange("to", e.target.value)}
                  className="mt-1 block rounded-md border px-3 py-2 text-sm"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Reset
                </button>
                <Button onClick={loadHistory} disabled={historyLoading || !selectedSite}>
                  {historyLoading ? "Refreshing…" : "Refresh"}
                </Button>
              </div>
            </div>
          </div>
          {historyError && <p className="mt-2 text-sm text-rose-600">{historyError}</p>}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="w-12 px-4 py-3 text-left">Details</th>
                  <th className="px-4 py-3 text-left">Batch ID</th>
                  <th className="px-4 py-3 text-left">Filename</th>
                  <th className="px-4 py-3 text-left">Site</th>
                  <th className="px-4 py-3 text-left">Start</th>
                  <th className="px-4 py-3 text-left">End</th>
                  <th className="px-4 py-3 text-left">Inserted</th>
                  <th className="px-4 py-3 text-left">Warnings</th>
                  <th className="px-4 py-3 text-left">Revoke</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-slate-500">
                      Loading history…
                    </td>
                  </tr>
                )}
                {!historyLoading && history.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-slate-500">
                      No import batches found for the selected filters.
                    </td>
                  </tr>
                )}
                {history.map((batch) => {
                  const isOpen = expandedBatches.includes(batch.id);
                  const siteName = sites.find((site) => site.id === batch.site_id)?.name || batch.site_id;
                  return (
                    <React.Fragment key={batch.id}>
                      <tr className="border-t hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => handleToggleBatch(batch.id)}
                            className="rounded-full border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600"
                          >
                            {isOpen ? "Hide" : "Show"}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-800">#{batch.id}</td>
                        <td className="px-4 py-3">{batch.source_filename || "—"}</td>
                        <td className="px-4 py-3">{siteName}</td>
                        <td className="px-4 py-3">{formatDateTime(batch.start_date)}</td>
                        <td className="px-4 py-3">{formatDateTime(batch.end_date)}</td>
                        <td className="px-4 py-3 font-medium text-emerald-700">{batch.rows_inserted ?? 0}</td>
                        <td className="px-4 py-3">{renderWarningsCell(batch)}</td>
                        <td className="px-4 py-3">
                          {batch.revoked_at ? (
                            <span className="text-sm font-semibold text-rose-600">Revoked</span>
                          ) : (
                            <Button
                              onClick={() => handleRevokeBatch(batch.id)}
                              disabled={revokingBatchId === batch.id}
                              className="border border-rose-400 bg-white px-3 py-2 text-sm font-semibold text-rose-600 shadow-sm transition hover:bg-rose-500 hover:text-white"
                            >
                              {revokingBatchId === batch.id ? "Revoking…" : "Revoke batch"}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={9} className="bg-slate-50 px-6 py-4">
                            {renderBatchFilesTable(batch)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Dialog
        open={preflightDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPreflightDialogOpen(false);
            setPreflightError("");
            setPreflightLoading(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>ZIP integrity check</DialogTitle>
          </DialogHeader>
          {preflightLoading && <p className="text-sm text-slate-600">Validating archive…</p>}
          {!preflightLoading && preflightError && (
            <p className="text-sm text-rose-600">{preflightError}</p>
          )}
          {!preflightLoading && !preflightError && preflightResult && (
            <div className="space-y-4 text-sm text-slate-700">
              <div className="flex items-center gap-2">
                <StatusTag
                  tone={
                    preflightResult.status === "ok"
                      ? "success"
                      : preflightResult.status === "error"
                      ? "error"
                      : "warning"
                  }
                >
                  {preflightResult.status === "ok" ? "Ready" : "Attention"}
                </StatusTag>
                <span>
                  {preflightResult.status === "ok"
                    ? "All required files are present."
                    : "Review the warnings before importing."}
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="text-xs uppercase text-slate-500">Total CSV files</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {preflightResult.total_csv_files ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="text-xs uppercase text-slate-500">Expected tids</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {preflightResult.expected_tids?.length ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="text-xs uppercase text-slate-500">Found tids</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {preflightResult.found_tids?.length ?? 0}
                  </p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase text-slate-500">Missing files</p>
                  {preflightResult.missing_files?.length ? (
                    <ul className="mt-2 list-disc space-y-1 pl-4 text-amber-700">
                      {preflightResult.missing_files.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-slate-600">None</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500">Extra files</p>
                  {preflightResult.extra_files?.length ? (
                    <ul className="mt-2 list-disc space-y-1 pl-4 text-amber-700">
                      {preflightResult.extra_files.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-slate-600">None</p>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Warnings</p>
                {preflightResult.warnings?.length ? (
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-amber-700">
                    {preflightResult.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-slate-600">No warnings.</p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">tids comparison</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(preflightResult.expected_tids || []).map((tid) => (
                    <span key={`expected-${tid}`} className="rounded-full bg-slate-200 px-2 py-0.5 font-mono text-xs">
                      {tid}
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(preflightResult.found_tids || []).map((tid) => (
                    <span key={`found-${tid}`} className="rounded-full bg-emerald-100 px-2 py-0.5 font-mono text-xs">
                      {tid}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setPreflightDialogOpen(false);
                setPreflightError("");
              }}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <Button
              onClick={() => handleZipCommit()}
              disabled={commitLoading || preflightLoading || !pendingZipFile}
            >
              {commitLoading ? "Importing…" : "Proceed with import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={summaryOpen}
        onOpenChange={(open) => {
          setSummaryOpen(open);
          if (!open) {
            setSummaryData(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import summary</DialogTitle>
          </DialogHeader>
          {summaryData ? (
            <div className="space-y-4 text-sm text-slate-700">
              {summaryData.duplicates_overwritten && (
                <p className="text-amber-600">
                  Existing rows were overwritten for this import.
                </p>
              )}
              <p>
                Inserted <strong>{summaryData.total_rows_upserted ?? 0}</strong> rows across{" "}
                <strong>{summaryData.file_count ?? 0}</strong> files.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">File</th>
                      <th className="px-3 py-2 text-left">Rows prepared</th>
                      <th className="px-3 py-2 text-left">Upserted</th>
                      <th className="px-3 py-2 text-left">Warnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summaryData.files || []).map((file) => {
                      const warningCount = file.warnings?.length || 0;
                      return (
                        <tr key={file.file_id} className="border-t">
                          <td className="px-3 py-2">{file.file_name}</td>
                          <td className="px-3 py-2">{file.rows_prepared ?? file.rows_received ?? 0}</td>
                          <td className="px-3 py-2 font-medium text-emerald-700">{file.rows_upserted ?? 0}</td>
                          <td className="px-3 py-2">
                            {warningCount ? (
                              <ul className="list-disc space-y-1 pl-4 text-xs text-amber-700">
                                {file.warnings.map((warning, idx) => (
                                  <li key={idx}>{warning}</li>
                                ))}
                              </ul>
                            ) : (
                              <StatusTag tone="success">None</StatusTag>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600">No summary available.</p>
          )}
          <div className="mt-6 flex justify-end">
            <Button onClick={() => setSummaryOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={duplicateDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDuplicateDialog({ open: false, files: [] });
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Existing data detected</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            The following files contain timestamps that already exist in SCADA data. Choose whether to overwrite the
            existing records or cancel the import.
          </p>
          <div className="mt-4 space-y-4 text-sm text-slate-700">
            {duplicateDialog.files.map((file, idx) => (
              <div key={`${file.file_name}-${idx}`} className="rounded-lg border bg-slate-50 p-3">
                <p className="font-semibold text-slate-900">{file.file_name}</p>
                <p className="text-slate-600">
                  {file.rows_conflicting} row{file.rows_conflicting === 1 ? "" : "s"} already exist for tid(s){" "}
                  {(file.tids || []).join(", ") || "unknown"}.
                </p>
                {file.sample_timestamps && file.sample_timestamps.length > 0 && (
                  <p className="text-xs text-slate-500">
                    Sample timestamps: {file.sample_timestamps.slice(0, 5).join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button
              onClick={() => setDuplicateDialog({ open: false, files: [] })}
              className="border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button onClick={handleOverwriteConfirm} disabled={commitLoading}>
              Overwrite existing data
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
